import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_NOTE } from '../graphql/mutations';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Define LocationPreference enum
const LocationPreference = {
  KIAWAH: 'KIAWAH',
  ISLE_OF_PALMS: 'ISLE_OF_PALMS',
  ISLE_OF_PALMS_WILD_DUNES: 'ISLE_OF_PALMS_WILD_DUNES',
  SEABROOK_ISLAND: 'SEABROOK_ISLAND'
};

// Define grouped area combinations
const AREA_GROUPS = {
  KIAWAH_SEABROOK: {
    value: 'KIAWAH_SEABROOK',
    label: 'Kiawah & Seabrook',
    areas: [LocationPreference.KIAWAH, LocationPreference.SEABROOK_ISLAND],
    isGroup: true
  },
  IOP_WILD_DUNES: {
    value: 'IOP_WILD_DUNES', 
    label: 'Isle of Palms & Wild Dunes',
    areas: [LocationPreference.ISLE_OF_PALMS, LocationPreference.ISLE_OF_PALMS_WILD_DUNES],
    isGroup: true
  }
};

// Available grouped areas for selection
const AVAILABLE_AREA_GROUPS = [
  AREA_GROUPS.KIAWAH_SEABROOK,
  AREA_GROUPS.IOP_WILD_DUNES
];

const AddNoteModal = ({ onClose, onSuccess, availableRoles, roleLabels }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedRoles, setSelectedRoles] = useState({});
  const [selectedAreaGroup, setSelectedAreaGroup] = useState(''); // Changed to single selection
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createNote, { loading: createNoteLoading }] = useMutation(CREATE_NOTE, {
    onCompleted: (data) => {
      console.log('Note created successfully:', data.createNote);
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(data.createNote);
      }
    },
    onError: (error) => {
      console.error('Error in createNote mutation:', error);
      setError(error.message || 'Failed to create note');
      setIsSubmitting(false);
    }
  });

  // Initialize with all roles unchecked by default except ADMIN which doesn't appear
  useEffect(() => {
    const initialRoles = availableRoles.reduce((acc, role) => {
      if (role !== 'ADMIN') {
        acc[role] = false;
      }
      return acc;
    }, {});
    setSelectedRoles(initialRoles);

    // Initialize area group selection as empty string (no group selected)
    setSelectedAreaGroup('');
  }, [availableRoles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setError('');
      
      if (!title.trim()) {
        throw new Error('Title is required');
      }
      
      if (!body.trim()) {
        throw new Error('Note content is required');
      }
      
      // Validate dates if both are provided
      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        throw new Error('Start date must be before end date');
      }
      
      // Get selected roles - admins will always see notes regardless of selection
      const visibleToRoles = Object.keys(selectedRoles)
        .filter(role => selectedRoles[role] && role !== 'ADMIN');
      
      if (visibleToRoles.length === 0) {
        throw new Error('Select at least one role that can view this note');
      }

      // Get selected areas from the selected group
      let visibleToAreas = null;
      if (selectedAreaGroup) {
        const selectedGroup = AREA_GROUPS[selectedAreaGroup];
        if (selectedGroup) {
          visibleToAreas = selectedGroup.areas;
        }
      }
      
      console.log('Creating note with roles:', visibleToRoles);
      console.log('Creating note with areas:', visibleToAreas);
      
      await createNote({
        variables: {
          input: {
            title,
            body,
            startDate: startDate || null,
            endDate: endDate || null,
            visibleToRoles,
            visibleToAreas: visibleToAreas
          }
        },
        update: (cache, { data }) => {
          if (data && data.createNote) {
            console.log('Note created, will update parent component');
          }
        }
      });
      
    } catch (err) {
      console.error('Error creating note:', err);
      setError(err.message || 'Failed to create note');
      setIsSubmitting(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet',
    'link'
  ];

  const handleRoleToggle = (role) => {
    setSelectedRoles({
      ...selectedRoles,
      [role]: !selectedRoles[role]
    });
  };

  const handleAreaGroupChange = (groupValue) => {
    setSelectedAreaGroup(groupValue);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl overflow-y-auto max-h-[90vh]">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Add New Note</h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-500"
            aria-label="Close modal"
            tabIndex="0"
            onKeyDown={(e) => e.key === 'Enter' && onClose()}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md">
              {error}
            </div>
          )}
          
          <div className="mb-4">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Note title"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500"
              required
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-1">
              Note Content *
            </label>
            <div className="border border-gray-300 rounded-md overflow-hidden">
              <ReactQuill
                theme="snow"
                value={body}
                onChange={setBody}
                modules={modules}
                formats={formats}
                placeholder="Write your note here..."
                className="h-40"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                Start Date (Optional)
              </label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500"
              />
            </div>
            
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                End Date (Optional)
              </label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500"
              />
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visible To Roles *
            </label>
            <div className="p-3 bg-gray-50 rounded-md mb-3">
              <p className="text-sm text-gray-600">
                <strong>Note:</strong> Admin users will always see all notes regardless of role selection.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
              {availableRoles.filter(role => role !== 'ADMIN').map(role => (
                <label key={role} className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={!!selectedRoles[role]}
                    onChange={() => handleRoleToggle(role)}
                    className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {roleLabels[role] || role}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visible To Areas (Optional)
            </label>
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md mb-3">
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> Users must match BOTH role AND area requirements to see this note. If no area group is selected, users only need to match the role requirement.
              </p>
            </div>
            <div className="space-y-2">
              {/* Option for no area restriction */}
              <label className="flex items-center p-2 border border-gray-200 rounded-md hover:bg-gray-50">
                <input
                  type="radio"
                  name="areaGroup"
                  value=""
                  checked={selectedAreaGroup === ''}
                  onChange={(e) => handleAreaGroupChange(e.target.value)}
                  className="text-sky-600 focus:ring-sky-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  All Areas (no restriction)
                </span>
              </label>
              
              {/* Area group options */}
              {AVAILABLE_AREA_GROUPS.map(group => (
                <label key={group.value} className="flex items-center p-2 border border-gray-200 rounded-md hover:bg-gray-50">
                  <input
                    type="radio"
                    name="areaGroup"
                    value={group.value}
                    checked={selectedAreaGroup === group.value}
                    onChange={(e) => handleAreaGroupChange(e.target.value)}
                    className="text-sky-600 focus:ring-sky-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {group.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || createNoteLoading}
              className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
            >
              {isSubmitting || createNoteLoading ? 'Creating...' : 'Create Note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNoteModal; 